import { useEffect } from 'react';
import { translations, type Language, useLanguage } from '@/contexts/LanguageContext';

const setMetaTag = (selector: string, attr: 'name' | 'property', content: string) => {
  if (typeof document === 'undefined') return;
  let element = document.querySelector(selector) as HTMLMetaElement | null;
  if (!element) {
    element = document.createElement('meta');
    const key = selector
      .replace(/^meta\[(name|property)="/, '')
      .replace(/"\]$/, '');
    element.setAttribute(attr, key);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
};

const setMetaByName = (name: string, content: string) =>
  setMetaTag(`meta[name="${name}"]`, 'name', content);
const setMetaByProperty = (property: string, content: string) =>
  setMetaTag(`meta[property="${property}"]`, 'property', content);

const normalizeSchemaText = (value: string) =>
  value.replace(/\s+/g, ' ').trim();

const SeoMeta = () => {
  const { language, t } = useLanguage();

  useEffect(() => {
    if (typeof document === 'undefined') return;

    const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://unfollowedstats.com';

    const rawPath = window.location.pathname || '/';
    const pathname = rawPath.replace(/\/+$/, '') || '/';
    const isRouteWithDedicatedSeo = /^\/(es|en|pt)\/(blog|terms|privacy)(\/|$)/.test(pathname);
    if (isRouteWithDedicatedSeo) {
      return;
    }
    const langMatch = pathname.match(/^\/(es|en|pt)(\/|$)/);
    const urlLang = langMatch?.[1] as 'es' | 'en' | 'pt' | undefined;
    const metaLang: Language = (urlLang ?? 'en') as Language;
    const seoCopy = translations[metaLang]?.landing?.seo;
    const title = seoCopy?.title ?? t('landing.seo.title');
    const description = seoCopy?.description ?? t('landing.seo.description');
    const keywords = seoCopy?.keywords ?? t('landing.seo.keywords');
    const effectiveLang = urlLang ?? (language as 'es' | 'en' | 'pt');
    const stripLangPrefix = (path: string) => path.replace(/^\/(es|en|pt)(?=\/|$)/, '');
    const basePath = stripLangPrefix(pathname) || '/';
    const ensureTrailingSlash = (path: string) => (path === '/' ? '/' : `${path}/`);
    const pathForLang = (lang: 'en' | 'es' | 'pt') => {
      return basePath === '/' ? `/${lang}/` : `/${lang}${ensureTrailingSlash(basePath)}`;
    };

    const normalizedSiteUrl = siteUrl.replace(/\/+$/, '');
    const canonicalPath = pathname === '/' ? '/' : `${pathname}/`;
    const canonicalUrl = `${normalizedSiteUrl}${canonicalPath}`;
    const hreflangs = {
      'x-default': `${normalizedSiteUrl}${pathForLang('en')}`,
      en: `${normalizedSiteUrl}${pathForLang('en')}`,
      es: `${normalizedSiteUrl}${pathForLang('es')}`,
      pt: `${normalizedSiteUrl}${pathForLang('pt')}`
    };
    const isDemoPath = basePath === '/demo' || /^\/demo\/?$/.test(basePath);
    const robotsContent = isDemoPath ? 'noindex, nofollow' : 'index, follow';
    const ogLocale = effectiveLang === 'es' ? 'es_ES' : effectiveLang === 'pt' ? 'pt_BR' : 'en_US';
    const ogImage = `${siteUrl}/icon-512.png`;
    const isRootPath = pathname === '/';
    const isLangPath = /^\/(es|en|pt)$/.test(pathname);

    document.title = title;
    document.documentElement.lang = effectiveLang;

    const baseKeywords = keywords
      .split(',')
      .map((keyword) => keyword.trim())
      .filter(Boolean);
    const extraKeywordsByLang: Record<'en' | 'es' | 'pt', string[]> = {
      en: [
        'how to see who unfollowed you on instagram without an app',
        'how to find who unfollowed me on instagram',
        'how to find who unfollowed me on instagram free',
        'how to tell who unfollowed you on instagram',
        'how to know who unfollowed me on instagram',
        'unfollowed on instagram free',
        'free app to let you know who unfollowed you from instagram',
        'recently unfollowed instagram'
      ],
      es: [
        'cómo ver quién te dejó de seguir en instagram sin una app',
        'cómo saber quién me dejó de seguir en instagram',
        'cómo saber quién me dejó de seguir en instagram gratis',
        'cómo ver quién te dejó de seguir en instagram',
        'quién me dejó de seguir en instagram gratis',
        'app gratis para saber quién te dejó de seguir en instagram',
        'dejó de seguir recientemente en instagram'
      ],
      pt: [
        'como ver quem deixou de me seguir no instagram sem um app',
        'como descobrir quem deixou de me seguir no instagram',
        'como descobrir quem deixou de me seguir no instagram grátis',
        'como saber quem deixou de me seguir no instagram',
        'quem deixou de me seguir no instagram grátis',
        'app grátis para saber quem deixou de me seguir no instagram',
        'deixou de seguir recentemente no instagram'
      ]
    };
    const extraKeywords = isDemoPath ? [] : extraKeywordsByLang[metaLang] ?? [];
    const seenKeywords = new Set<string>();
    const dedupedKeywords = [...baseKeywords, ...extraKeywords].filter((keyword) => {
      const normalized = keyword.toLowerCase();
      if (seenKeywords.has(normalized)) return false;
      seenKeywords.add(normalized);
      return true;
    });
    const finalKeywords = dedupedKeywords.join(', ');

    setMetaByName('description', description);
    setMetaByName('keywords', finalKeywords);
    setMetaByName('google', 'notranslate');
    setMetaByName('robots', robotsContent);
    setMetaByName('googlebot', robotsContent);
    setMetaByProperty('og:title', title);
    setMetaByProperty('og:description', description);
    setMetaByProperty('og:url', canonicalUrl);
    setMetaByProperty('og:image', ogImage);
    setMetaByProperty('og:locale', ogLocale);
    setMetaByName('twitter:title', title);
    setMetaByName('twitter:description', description);
    setMetaByName('twitter:image', ogImage);

    const canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (canonical) {
      canonical.href = canonicalUrl;
    } else {
      const link = document.createElement('link');
      link.rel = 'canonical';
      link.href = canonicalUrl;
      document.head.appendChild(link);
    }

    const setHreflang = (code: string, href: string) => {
      let element = document.querySelector(
        `link[rel="alternate"][hreflang="${code}"]`
      ) as HTMLLinkElement | null;
      if (!element) {
        element = document.createElement('link');
        element.rel = 'alternate';
        element.hreflang = code;
        document.head.appendChild(element);
      }
      element.href = href;
    };

    setHreflang('x-default', hreflangs['x-default']);
    setHreflang('en', hreflangs.en);
    setHreflang('es', hreflangs.es);
    setHreflang('pt', hreflangs.pt);

    const faqScriptId = 'faq-schema';
    const hasFaqSection = Boolean(document.getElementById('faq'));
    const shouldIncludeFaqSchema = (isRootPath || isLangPath) && hasFaqSection;
    const existingFaqScript = document.getElementById(faqScriptId) as HTMLScriptElement | null;
    if (shouldIncludeFaqSchema) {
      const faqKeys = [
        'safeUnfollowed',
        'accountSafety',
        'accuracy',
        'autoTracking',
        'whyDownload',
        'generationTime',
        'unfollowNotifications',
        'blockedUsers'
      ];
      const faqSchema = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqKeys.map((key) => ({
          '@type': 'Question',
          name: normalizeSchemaText(t(`landing.faq.items.${key}.question`)),
          acceptedAnswer: {
            '@type': 'Answer',
            text: normalizeSchemaText(t(`landing.faq.items.${key}.answer`))
          }
        }))
      };
      const schemaJson = JSON.stringify(faqSchema);
      if (existingFaqScript) {
        existingFaqScript.textContent = schemaJson;
      } else {
        const script = document.createElement('script');
        script.id = faqScriptId;
        script.type = 'application/ld+json';
        script.textContent = schemaJson;
        document.head.appendChild(script);
      }
    } else if (existingFaqScript) {
      existingFaqScript.remove();
    }
  }, [language, t]);

  return null;
};

export default SeoMeta;
